<template>
  <div>
    <v-card>
      <v-card-title>
        Reports
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-list two-line outlined rounded>
              <v-list-item-group v-model="selectedReportId" color="primary">
                <v-list-item
                  v-for="report in reportOptions"
                  :key="report.id"
                  :value="report.id"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ report.label }}</v-list-item-title>
                    <v-list-item-subtitle>{{ report.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-col>

          <v-col cols="12" md="8">
            <v-card outlined class="report-panel">
              <v-card-title class="text-subtitle-1 font-weight-bold">
                {{ selectedReport?.label || 'Select a report' }}
              </v-card-title>
              <v-card-text>
                <div v-if="selectedReport" class="text-body-2 mb-4">
                  {{ selectedReport.description }}
                </div>
                <div v-else class="text-body-2 grey--text">
                  Choose a report from the list to export it.
                </div>

                <div v-if="selectedReport" class="mb-4">
                  <div class="text-caption grey--text text--darken-1 mb-2">
                    Columns
                  </div>
                  <v-chip
                    v-for="column in selectedReport.columns"
                    :key="column.key"
                    small
                    class="mr-2 mb-2"
                  >
                    {{ column.label }}
                  </v-chip>
                </div>

                <div v-if="isBookedLessonsReport" class="mb-4">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="isStartDateMenuVisible"
                        :close-on-content-click="false"
                        offset-y
                        content-class="report-date-menu"
                        min-width="320"
                        max-width="320"
                      >
                        <template #activator="{ attrs }">
                          <v-text-field
                            :value="reportFilters.startDate"
                            label="Start Date"
                            class="report-date-field"
                            outlined
                            dense
                            hide-details
                            readonly
                            :append-icon="icons.mdiCalendarMonth"
                            v-bind="attrs"
                            @click="isStartDateMenuVisible = true"
                            @click:append="isStartDateMenuVisible = true"
                          />
                        </template>

                        <v-date-picker
                          v-model="reportFilters.startDate"
                          locale="en-GB"
                          first-day-of-week="1"
                          class="compact-date-picker"
                          @input="handleDatePicked('startDate')"
                        >
                          <v-spacer />
                          <v-btn x-small text class="compact-date-action" @click="clearDateFilter('startDate')">
                            Clear
                          </v-btn>
                          <v-btn x-small text class="compact-date-action" @click="setTodayDateFilter('startDate')">
                            Today
                          </v-btn>
                          <v-btn x-small text class="compact-date-action" @click="isStartDateMenuVisible = false">
                            Cancel
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="isEndDateMenuVisible"
                        :close-on-content-click="false"
                        offset-y
                        content-class="report-date-menu"
                        min-width="320"
                        max-width="320"
                      >
                        <template #activator="{ attrs }">
                          <v-text-field
                            :value="reportFilters.endDate"
                            label="End Date"
                            class="report-date-field"
                            outlined
                            dense
                            hide-details
                            readonly
                            :append-icon="icons.mdiCalendarMonth"
                            v-bind="attrs"
                            @click="isEndDateMenuVisible = true"
                            @click:append="isEndDateMenuVisible = true"
                          />
                        </template>

                        <v-date-picker
                          v-model="reportFilters.endDate"
                          locale="en-GB"
                          first-day-of-week="1"
                          class="compact-date-picker"
                          @input="handleDatePicked('endDate')"
                        >
                          <v-spacer />
                          <v-btn x-small text class="compact-date-action" @click="clearDateFilter('endDate')">
                            Clear
                          </v-btn>
                          <v-btn x-small text class="compact-date-action" @click="setTodayDateFilter('endDate')">
                            Today
                          </v-btn>
                          <v-btn x-small text class="compact-date-action" @click="isEndDateMenuVisible = false">
                            Cancel
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="reportFilters.coachId"
                        :items="coachOptions"
                        label="Coach"
                        outlined
                        dense
                        hide-details
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="reportFilters.playerId"
                        :items="playerOptions"
                        label="Player"
                        outlined
                        dense
                        hide-details
                        clearable
                      />
                    </v-col>
                  </v-row>
                </div>

                <div v-if="selectedReport" class="d-flex flex-wrap gap-2">
                  <v-btn color="secondary" outlined :loading="isPreviewLoading" @click="previewReport">
                    Preview
                  </v-btn>
                  <v-btn color="primary" :loading="isExporting && exportType === 'csv'" @click="exportReport('csv')">
                    Export to CSV
                  </v-btn>
                  <v-btn color="primary" outlined :loading="isExporting && exportType === 'excel'" @click="exportReport('excel')">
                    Export to Excel
                  </v-btn>
                  <v-btn text @click="clearSelection">
                    Cancel
                  </v-btn>
                </div>

                <div v-if="selectedReport && previewRows.length" class="mt-6">
                  <div class="text-caption grey--text text--darken-1 mb-2">
                    Preview (first {{ Math.min(previewRows.length, 10) }} rows)
                  </div>
                  <v-data-table
                    :headers="previewHeaders"
                    :items="previewRows"
                    dense
                    disable-pagination
                    hide-default-footer
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  computed, ref, watch,
} from 'vue'
import { mdiCalendarMonth } from '@mdi/js'
import * as XLSX from 'xlsx'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAuth from '@/composables/useAuth'

const csvCell = value => `"${String(value ?? '').replace(/"/g, '""')}"`

const downloadBlob = (content, mimeType, filename) => {
  const blob = new Blob([content], { type: mimeType })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  link.click()

  window.URL.revokeObjectURL(url)
}

const exportRowsToCsv = (rows, columns, filename) => {
  const lines = [
    columns.map(column => csvCell(column.label)).join(','),
    ...rows.map(row => columns.map(column => csvCell(row[column.key])).join(',')),
  ]

  downloadBlob(lines.join('\n'), 'text/csv;charset=utf-8;', `${filename}.csv`)
}

const exportRowsToExcel = (rows, columns, filename) => {
  const worksheetRows = rows.map(row => columns.reduce((accumulator, column) => {
    accumulator[column.label] = row[column.key] ?? ''

    return accumulator
  }, {}))

  const worksheet = XLSX.utils.json_to_sheet(worksheetRows, {
    header: columns.map(column => column.label),
  })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report')
  XLSX.writeFile(workbook, `${filename}.xlsx`)
}

const formatDateLabel = value => {
  if (!value) return ''

  return new Date(value).toLocaleDateString('en-GB')
}

const formatTimeLabel = timeSlot => {
  if (timeSlot?.startTimeTZ) return timeSlot.startTimeTZ
  if (!timeSlot?.startTimestamp) return ''

  return new Date(timeSlot.startTimestamp).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDurationLabel = timeSlot => {
  if (!timeSlot?.startTimestamp || !timeSlot?.endTimestamp) return ''

  const start = new Date(timeSlot.startTimestamp).getTime()
  const end = new Date(timeSlot.endTimestamp).getTime()
  const durationMinutes = Math.max(Math.round((end - start) / 60000), 0)
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const startOfDayIso = value => `${value}T00:00:00.000Z`
const endOfDayIso = value => `${value}T23:59:59.999Z`

const enumerateDateChunks = (startDate, endDate) => {
  const chunks = []
  const current = new Date(`${startDate}T00:00:00.000Z`)
  const end = new Date(`${endDate}T00:00:00.000Z`)

  while (current <= end) {
    const chunkDate = current.toISOString().slice(0, 10)
    chunks.push({
      from: startOfDayIso(chunkDate),
      to: endOfDayIso(chunkDate),
    })
    current.setUTCDate(current.getUTCDate() + 1)
  }

  return chunks
}

export default {
  name: 'ReportsView',
  setup() {
    const { showSnackbar } = useSnackbar()
    const { getValidToken } = useAuth()

    const academies = ref([])
    const users = ref([])
    const contentPacks = ref([])

    const selectedReportId = ref(null)
    const isExporting = ref(false)
    const isPreviewLoading = ref(false)
    const exportType = ref(null)
    const previewRows = ref([])
    const sharedDataLoaded = ref(false)
    const isStartDateMenuVisible = ref(false)
    const isEndDateMenuVisible = ref(false)
    const reportFilters = ref({
      startDate: '',
      endDate: '',
      coachId: null,
      playerId: null,
    })

    const requestReport = async (path, options = {}) => {
      const token = await getValidToken()
      const baseUrl = process.env.VUE_APP_API_BASE_URL || ''
      const response = await fetch(`${baseUrl}${path}`, {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(options.headers || {}),
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch ${path}: ${response.status}`)
      }

      return response.json()
    }

    const allReportOptions = [
      {
        id: 'academy-content-types',
        label: 'Academy to Content Types',
        description: 'Academy info mapped to content types available through assigned content packs.',
        filename: 'academy-to-content-types',
        columns: [
          { key: 'academyName', label: 'Academy Name' },
          { key: 'academyEmail', label: 'Academy Email' },
          { key: 'academyHeadCoach', label: 'Academy Head Coach' },
          { key: 'contentType', label: 'Content Type' },
          { key: 'contentName', label: 'Content Name' },
        ],
      },
      {
        id: 'academy-content-packs',
        label: 'Academy to Content Packs',
        description: 'Academy info mapped to assigned content packs.',
        filename: 'academy-to-content-packs',
        columns: [
          { key: 'academyName', label: 'Academy Name' },
          { key: 'academyEmail', label: 'Academy Email' },
          { key: 'academyHeadCoach', label: 'Academy Head Coach' },
          { key: 'contentPackName', label: 'Content Pack Name' },
        ],
      },
      {
        id: 'academy-players',
        label: 'Academy to Members',
        description: 'Academy info mapped to academy members.',
        filename: 'academy-to-members',
        columns: [
          { key: 'academyName', label: 'Academy Name' },
          { key: 'academyEmail', label: 'Academy Email' },
          { key: 'academyHeadCoach', label: 'Academy Head Coach' },
          { key: 'userRole', label: 'Role' },
          { key: 'userFirstName', label: 'First Name' },
          { key: 'userLastName', label: 'Last Name' },
          { key: 'userEmail', label: 'Email' },
        ],
      },
      {
        id: 'users-content-packs',
        label: 'Users to Content Packs',
        description: 'Users mapped to directly assigned content packs.',
        filename: 'users-to-content-packs',
        columns: [
          { key: 'userRole', label: 'Role' },
          { key: 'userFirstName', label: 'First Name' },
          { key: 'userLastName', label: 'Last Name' },
          { key: 'userEmail', label: 'Email' },
          { key: 'contentPackName', label: 'Content Pack Name' },
        ],
      },
      {
        id: 'content-packs-academies',
        label: 'Content Packs to Academies',
        description: 'Content packs mapped to academies.',
        filename: 'content-packs-to-academies',
        columns: [
          { key: 'contentPackName', label: 'Content Pack Name' },
          { key: 'academyName', label: 'Academy Name' },
          { key: 'academyEmail', label: 'Academy Email' },
          { key: 'academyHeadCoach', label: 'Academy Head Coach' },
        ],
      },
      {
        id: 'content-packs-users',
        label: 'Content Packs to Users',
        description: 'Content packs mapped to directly assigned users.',
        filename: 'content-packs-to-users',
        columns: [
          { key: 'contentPackName', label: 'Content Pack Name' },
          { key: 'userRole', label: 'Role' },
          { key: 'userFirstName', label: 'First Name' },
          { key: 'userLastName', label: 'Last Name' },
          { key: 'userEmail', label: 'Email' },
        ],
      },
      {
        id: 'booked-lessons',
        label: 'Booked Sessions',
        description: 'Lessons and sessions for a selected date range, coach and player.',
        filename: 'booked-sessions',
        columns: [
          { key: 'playerFirstName', label: 'Player First Name' },
          { key: 'playerLastName', label: 'Player Last Name' },
          { key: 'playerEmail', label: 'Player Email' },
          { key: 'date', label: 'Date' },
          { key: 'time', label: 'Time' },
          { key: 'status', label: 'Status' },
          { key: 'startTime', label: 'Start Time' },
          { key: 'endTime', label: 'End Time' },
          { key: 'serviceType', label: 'Service Type' },
        ],
      },
    ]

    const reportOptions = computed(() => allReportOptions)
    const selectedReport = computed(() => reportOptions.value.find(report => report.id === selectedReportId.value) || null)
    const isBookedLessonsReport = computed(() => selectedReport.value?.id === 'booked-lessons')
    const previewHeaders = computed(() => (selectedReport.value?.columns || []).map(column => ({
      text: column.label,
      value: column.key,
      sortable: false,
    })))
    const formatUserOptionLabel = user => {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()

      if (fullName && user.email) return `${fullName} (${user.email})`

      return fullName || user.email || user.id
    }

    const coachOptions = computed(() => users.value
      .filter(user => String(user.type || '').toLowerCase() === 'coach')
      .sort((left, right) => `${left.lastName || ''}|${left.firstName || ''}|${left.email || ''}`.localeCompare(
        `${right.lastName || ''}|${right.firstName || ''}|${right.email || ''}`,
      ))
      .map(user => ({
        text: formatUserOptionLabel(user),
        value: user.id,
      })))
    const playerOptions = computed(() => {
      const selectedCoachId = reportFilters.value.coachId
      const players = users.value.filter(user => String(user.type || '').toLowerCase() === 'athlete')
      const coachScopedPlayers = selectedCoachId
        ? players.filter(user => !user.coach || user.coach === selectedCoachId)
        : players
      const finalPlayers = coachScopedPlayers.length ? coachScopedPlayers : players

      return [
        { text: 'All', value: null },
        ...finalPlayers
          .sort((left, right) => `${left.lastName || ''}|${left.firstName || ''}|${left.email || ''}`.localeCompare(
            `${right.lastName || ''}|${right.firstName || ''}|${right.email || ''}`,
          ))
          .map(user => ({
            text: formatUserOptionLabel(user),
            value: user.id,
          })),
      ]
    })

    const clearSelection = () => {
      selectedReportId.value = null
      previewRows.value = []
      reportFilters.value = {
        startDate: '',
        endDate: '',
        coachId: null,
        playerId: null,
      }
    }

    const todayDate = () => new Date().toISOString().slice(0, 10)

    const clearDateFilter = field => {
      reportFilters.value[field] = ''
    }

    const setTodayDateFilter = field => {
      reportFilters.value[field] = todayDate()
      if (field === 'startDate') {
        isStartDateMenuVisible.value = false
      } else {
        isEndDateMenuVisible.value = false
      }
    }

    const handleDatePicked = field => {
      if (field === 'startDate') {
        isStartDateMenuVisible.value = false
      } else {
        isEndDateMenuVisible.value = false
      }
    }

    const normalizeRole = role => {
      const value = String(role || '').toLowerCase()

      if (value === 'athlete') return 'Player'
      if (value === 'coach') return 'Coach'
      if (value === 'admin') return 'Admin'

      return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : ''
    }

    const academyInfoMap = academiesList => academiesList.reduce((accumulator, academy) => {
      accumulator[academy.id] = {
        academyName: academy.name || '',
        academyEmail: academy.email || '',
        academyHeadCoach: academy.headCoach || '',
      }

      return accumulator
    }, {})

    const userInfoMap = usersList => usersList.reduce((accumulator, user) => {
      accumulator[user.id] = {
        userRole: normalizeRole(user.type),
        userFirstName: user.firstName || '',
        userLastName: user.lastName || '',
        userEmail: user.email || '',
      }

      return accumulator
    }, {})

    const loadSharedData = async () => {
      const [academiesResponse, usersResponse, contentPacksResponse] = await Promise.all([
        requestReport('/company'),
        requestReport('/user/searchUsers?size=2000&sort=lastName,asc&sort=firstName,asc', {
          method: 'POST',
          body: {},
        }),
        requestReport('/content-pack'),
      ])

      academies.value = academiesResponse || []
      users.value = usersResponse?.content || []
      contentPacks.value = Array.isArray(contentPacksResponse)
        ? contentPacksResponse
        : (contentPacksResponse?.contentPacks || contentPacksResponse?.items || contentPacksResponse?.data || [])
      sharedDataLoaded.value = true
    }

    const ensureSharedDataLoaded = async () => {
      if (sharedDataLoaded.value) return
      await loadSharedData()
    }

    const isActiveAndNotDeleted = item => item?.active === true && item?.deleted !== true

    const resolveAcademyId = item => item?.academyId ?? item?.companyId ?? null

    const uniqueBy = (items, getKey) => {
      const seen = new Set()

      return items.filter(item => {
        const key = getKey(item)
        if (seen.has(key)) return false
        seen.add(key)

        return true
      })
    }

    const sortBy = (items, getValue) => [...items].sort((left, right) => getValue(left).localeCompare(getValue(right)))

    const expandOrFallback = (baseRow, items, buildRow, emptyFields) => {
      if (!items.length) {
        return [{ ...baseRow, ...emptyFields }]
      }

      return items.map(item => ({
        ...baseRow,
        ...buildRow(item),
      }))
    }

    const contentTypeLabel = (category, resourceType) => {
      const normalizedCategory = String(category || '').toLowerCase()

      if (normalizedCategory === 'fitness' && resourceType === 'exercise') return 'Fitness Exercise'
      if (normalizedCategory === 'fitness' && resourceType === 'session') return 'Fitness Routine'
      if (normalizedCategory === 'fitness' && resourceType === 'assessment') return 'Fitness Assessment'
      if (normalizedCategory === 'practice' && resourceType === 'exercise') return 'Practice Drill'
      if (normalizedCategory === 'practice' && resourceType === 'session') return 'Practice Session'
      if (normalizedCategory === 'practice' && resourceType === 'assessment') return 'Golf Skills Assessment'
      if (normalizedCategory === 'flow' && resourceType === 'exercise') return 'The Flow Exercise'
      if (normalizedCategory === 'flow' && resourceType === 'session') return 'The Flow Session'
      if (normalizedCategory === 'flow' && resourceType === 'assessment') return 'The Flow Assessment'

      return resourceType
    }

    const fetchContentCatalog = async () => {
      const [exercisesResponse, sessionsResponse, assessmentsResponse] = await Promise.all([
        requestReport('/exercise?withCreator=true&allAcademies=true'),
        requestReport('/session?withCreator=true&allAcademies=true'),
        requestReport('/assessment/group?withCreator=true&allAcademies=true'),
      ])

      const buildCatalogItems = (items, resourceType) => items
        .filter(isActiveAndNotDeleted)
        .map(item => ({
          id: String(item.id || item._id),
          academyId: resolveAcademyId(item),
          contentType: contentTypeLabel(item.category, resourceType),
          contentName: item.name || '',
        }))

      const allItems = [
        ...buildCatalogItems(exercisesResponse || [], 'exercise'),
        ...buildCatalogItems(sessionsResponse || [], 'session'),
        ...buildCatalogItems(assessmentsResponse || [], 'assessment'),
      ]

      return {
        byId: allItems.reduce((accumulator, item) => {
          accumulator[item.id] = item

          return accumulator
        }, {}),
        globalItems: sortBy(
          allItems.filter(item => !item.academyId),
          item => `${item.contentType}|${item.contentName}`,
        ),
      }
    }

    const getAcademyAssignedContentPacks = academyId => sortBy(
      contentPacks.value.filter(contentPack => (contentPack.academies || []).some(academy => academy.id === academyId)),
      item => item.displayName || '',
    )

    const fetchUsersByContentPackMap = async () => {
      const result = {}

      // Sequential on purpose to avoid sending a very large batch at once.
      // eslint-disable-next-line no-restricted-syntax
      for (const contentPack of contentPacks.value) {
        // eslint-disable-next-line no-await-in-loop
        result[contentPack.id] = await requestReport(`/content-pack-user/content-pack/${contentPack.id}/users`)
      }

      return result
    }

    const fetchAcademyUsersForReport = academyId => requestReport('/company/users/search', {
      method: 'POST',
      body: { companyId: academyId },
    }).then(response => response?.users || [])

    const validateBookedLessonsFilters = () => {
      if (!reportFilters.value.startDate || !reportFilters.value.endDate) {
        showSnackbar('Select both start and end date for Booked Sessions report', SnackbarType.ERROR)

        return false
      }

      if (!reportFilters.value.coachId) {
        showSnackbar('Select a coach for Booked Sessions report', SnackbarType.ERROR)

        return false
      }

      if (reportFilters.value.startDate > reportFilters.value.endDate) {
        showSnackbar('Start date cannot be after end date', SnackbarType.ERROR)

        return false
      }

      return true
    }

    const fetchBookedLessonsTimeSlots = async () => {
      const chunks = enumerateDateChunks(reportFilters.value.startDate, reportFilters.value.endDate)
      const rows = []

      // Sequential on purpose to avoid hitting the booking endpoint limit for wider ranges.
      // eslint-disable-next-line no-restricted-syntax
      for (const chunk of chunks) {
        const queryParams = new URLSearchParams({
          timestampFrom: chunk.from,
          timestampTo: chunk.to,
          coachId: reportFilters.value.coachId,
        })

        if (reportFilters.value.playerId) {
          queryParams.append('playerId', reportFilters.value.playerId)
        }

        // eslint-disable-next-line no-await-in-loop
        const response = await requestReport(`/timeSlot/?${queryParams.toString()}`)
        rows.push(...(Array.isArray(response) ? response : []))
      }

      return rows
    }

    const buildReportRows = async reportId => {
      await ensureSharedDataLoaded()

      const academyMap = academyInfoMap(academies.value)
      const userMap = userInfoMap(users.value)

      if (reportId === 'academy-content-packs') {
        return academies.value.flatMap(academy => getAcademyAssignedContentPacks(academy.id).map(contentPack => ({
          ...academyMap[academy.id],
          contentPackName: contentPack.displayName || '',
        })))
      }

      if (reportId === 'content-packs-academies') {
        return contentPacks.value.flatMap(contentPack => expandOrFallback(
          { contentPackName: contentPack.displayName || '' },
          sortBy(contentPack.academies || [], academy => academy.name || ''),
          academy => ({
            ...(academyMap[academy.id] || {
              academyName: academy.name || '',
              academyEmail: '',
              academyHeadCoach: '',
            }),
          }),
          {
            academyName: '',
            academyEmail: '',
            academyHeadCoach: '',
          },
        ))
      }

      if (reportId === 'academy-content-types') {
        const contentCatalog = await fetchContentCatalog()
        const globalRows = expandOrFallback(
          {
            academyName: 'GLOBAL CONTENT',
            academyEmail: '',
            academyHeadCoach: '',
          },
          contentCatalog.globalItems,
          item => ({
            contentType: item.contentType,
            contentName: item.contentName,
          }),
          {
            contentType: '',
            contentName: '',
          },
        )

        const academyRows = academies.value.flatMap(academy => {
          const directItems = Object.values(contentCatalog.byId)
            .filter(item => item.academyId === academy.id)

          const deduplicatedItems = sortBy(
            uniqueBy(directItems, item => item.id),
            item => `${item.contentType}|${item.contentName}`,
          )

          return expandOrFallback(
            academyMap[academy.id],
            deduplicatedItems,
            item => ({
              contentType: item.contentType,
              contentName: item.contentName,
            }),
            {
              contentType: '',
              contentName: '',
            },
          )
        })

        return [...globalRows, ...academyRows]
      }

      if (reportId === 'academy-players') {
        const rows = []

        // Sequential on purpose to avoid sending a very large batch at once.
        // eslint-disable-next-line no-restricted-syntax
        for (const academy of academies.value) {
          // eslint-disable-next-line no-await-in-loop
          const academyUsers = await fetchAcademyUsersForReport(academy.id)
          const resolvedHeadCoach = academyUsers?.[0]
            ? `${academyUsers[0].firstName || ''} ${academyUsers[0].lastName || ''}`.trim()
            : (academyMap[academy.id]?.academyHeadCoach || '')

          rows.push(...expandOrFallback(
            {
              ...academyMap[academy.id],
              academyHeadCoach: resolvedHeadCoach,
            },
            sortBy(academyUsers || [], user => `${user.lastName || ''}|${user.firstName || ''}|${user.email || ''}`),
            user => ({
              userRole: normalizeRole(user.userType),
              userFirstName: user.firstName || '',
              userLastName: user.lastName || '',
              userEmail: user.email || '',
            }),
            {
              userRole: '',
              userFirstName: '',
              userLastName: '',
              userEmail: '',
            },
          ))
        }

        return rows
      }

      if (reportId === 'content-packs-users') {
        const rows = []
        const usersByContentPackId = await fetchUsersByContentPackMap()

        // eslint-disable-next-line no-restricted-syntax
        for (const contentPack of contentPacks.value) {
          rows.push(...expandOrFallback(
            { contentPackName: contentPack.displayName || '' },
            sortBy(usersByContentPackId[contentPack.id] || [], user => `${user.lastName || ''}|${user.firstName || ''}|${user.email || ''}`),
            user => ({
              ...(userMap[user.id] || {
                userRole: normalizeRole(user.type),
                userFirstName: user.firstName || '',
                userLastName: user.lastName || '',
                userEmail: user.email || '',
              }),
            }),
            {
              userRole: '',
              userFirstName: '',
              userLastName: '',
              userEmail: '',
            },
          ))
        }

        return rows
      }

      if (reportId === 'users-content-packs') {
        const rows = []
        const usersByContentPackId = await fetchUsersByContentPackMap()

        // eslint-disable-next-line no-restricted-syntax
        for (const user of sortBy(users.value, item => `${item.lastName || ''}|${item.firstName || ''}|${item.email || ''}`)) {
          const assignedContentPacks = sortBy(
            contentPacks.value.filter(contentPack => (usersByContentPackId[contentPack.id] || []).some(item => item.id === user.id)),
            item => item.displayName || '',
          )

          rows.push(...assignedContentPacks.map(contentPack => ({
            ...(userMap[user.id] || {
              userRole: normalizeRole(user.type),
              userFirstName: user.firstName || '',
              userLastName: user.lastName || '',
              userEmail: user.email || '',
            }),
            contentPackName: contentPack.displayName || '',
          })))
        }

        return rows
      }

      if (reportId === 'booked-lessons') {
        if (!validateBookedLessonsFilters()) return null

        const usersById = users.value.reduce((accumulator, user) => {
          accumulator[user.id] = user

          return accumulator
        }, {})
        const timeSlots = await fetchBookedLessonsTimeSlots()

        return sortBy(
          timeSlots.map(timeSlot => {
            const player = usersById[timeSlot.playerId] || {}

            return {
              playerFirstName: player.firstName || '',
              playerLastName: player.lastName || '',
              playerEmail: player.email || '',
              date: formatDateLabel(timeSlot.startTimestamp),
              time: formatDurationLabel(timeSlot),
              status: timeSlot.status || '',
              startTime: formatTimeLabel(timeSlot),
              endTime: timeSlot.endTimeTZ || '',
              serviceType: timeSlot.type || '',
            }
          }),
          row => `${row.date}|${row.startTime}|${row.endTime}|${row.status}|${row.playerLastName}|${row.playerFirstName}|${row.playerEmail}`,
        )
      }

      return []
    }

    const previewReport = async () => {
      if (!selectedReport.value) return

      isPreviewLoading.value = true
      try {
        const rows = await buildReportRows(selectedReport.value.id)
        if (rows === null) return

        previewRows.value = rows.slice(0, 10)
      } catch (error) {
        console.error(error)
        showSnackbar('Failed to build report preview', SnackbarType.ERROR)
      } finally {
        isPreviewLoading.value = false
      }
    }

    const exportReport = async type => {
      if (!selectedReport.value) return

      isExporting.value = true
      exportType.value = type

      try {
        const rows = await buildReportRows(selectedReport.value.id)
        if (rows === null) return

        previewRows.value = rows.slice(0, 10)

        if (type === 'csv') {
          exportRowsToCsv(rows, selectedReport.value.columns, selectedReport.value.filename)
        } else {
          exportRowsToExcel(rows, selectedReport.value.columns, selectedReport.value.filename)
        }

        showSnackbar('Report exported successfully', SnackbarType.SUCCESS)
      } catch (error) {
        console.error(error)
        showSnackbar('Failed to export report', SnackbarType.ERROR)
      } finally {
        isExporting.value = false
        exportType.value = null
      }
    }

    watch(() => selectedReportId.value, async nextValue => {
      previewRows.value = []

      if (nextValue === 'booked-lessons') {
        try {
          await ensureSharedDataLoaded()
        } catch (error) {
          console.error(error)
        }
      }
    })

    watch(() => reportFilters.value.coachId, () => {
      if (reportFilters.value.playerId && !playerOptions.value.some(option => option.value === reportFilters.value.playerId)) {
        reportFilters.value.playerId = null
      }
      previewRows.value = []
    })

    watch(() => [reportFilters.value.startDate, reportFilters.value.endDate, reportFilters.value.playerId], () => {
      previewRows.value = []
    })

    return {
      icons: {
        mdiCalendarMonth,
      },
      reportOptions,
      selectedReportId,
      selectedReport,
      isBookedLessonsReport,
      isStartDateMenuVisible,
      isEndDateMenuVisible,
      reportFilters,
      coachOptions,
      playerOptions,
      isExporting,
      isPreviewLoading,
      exportType,
      previewRows,
      previewHeaders,
      clearDateFilter,
      setTodayDateFilter,
      handleDatePicked,
      previewReport,
      exportReport,
      clearSelection,
    }
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.report-panel {
  min-height: 320px;
}

.report-date-field :deep(.v-icon) {
  transition: color 0.2s ease;
}

.report-date-field :deep(.v-input__append-inner) {
  cursor: pointer;
}

.theme--light .report-date-field :deep(.v-icon) {
  color: rgba(0, 0, 0, 0.6) !important;
}

.theme--dark .report-date-field :deep(.v-icon) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.compact-date-picker {
  width: 320px;
}

:deep(.report-date-menu) {
  margin-top: 4px;
}

:deep(.report-date-menu .v-picker) {
  width: 320px;
}

.compact-date-action {
  min-width: 0;
  padding: 0 6px !important;
  font-size: 11px !important;
}

.compact-date-picker :deep(.v-date-picker-header) {
  padding: 8px 10px 4px;
}

.compact-date-picker :deep(.v-date-picker-header__value) {
  font-size: 14px;
  line-height: 1.2;
}

.compact-date-picker :deep(.v-date-picker-table) {
  height: auto;
}

.compact-date-picker :deep(.v-date-picker-table table) {
  font-size: 12px;
}

.compact-date-picker :deep(.v-btn) {
  letter-spacing: 0;
}

.compact-date-picker :deep(.v-date-picker-table .v-btn) {
  width: 30px;
  height: 30px;
  margin: 0;
}

.compact-date-picker :deep(.v-date-picker-table th),
.compact-date-picker :deep(.v-date-picker-table td) {
  padding: 0;
}

.compact-date-picker :deep(.v-picker__actions) {
  padding: 4px 8px 8px;
  gap: 4px;
}
</style>
